import { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  connectToHubNotificationRestaurante,
  connectToHubNotificationSession,
} from "src/redux/slices/hub";

import notifyme from "../notifyme";
import {
  addNotificationsReady,
  readNotification,
  receiveNotificationFromHubSuccess,
} from "src/redux/slices/notifications";
import { fetchScheduleAlerts } from "src/redux/slices/scheduleAlerts";
import { pollEmployeeCorrectionsCount } from "src/redux/slices/employeeCorrections";
import buildHubUrl from "./buildHubUrl";
import CreateConnection from "./HubMatrix";

export function useNotificationRestauranteHub() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authJwt);
  const isConnectedRef = useRef(false); // Para evitar múltiples conexiones

  // Función optimizada para manejar la recepción de notificaciones
  const handleReceiveNotification = useCallback(
    (notification) => {
      dispatch(receiveNotificationFromHubSuccess(notification));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!user?.id || isConnectedRef.current) return;

    isConnectedRef.current = true; // Evita conexiones repetidas

    const cleanup = CreateConnection(
      buildHubUrl("/hub/notificationrestaurantehub", { restauranteId: user.id }),
      [
        {
          id: `notification/${user.id}`,
          operator: handleReceiveNotification,
        },
        // Push del backend cuando cambia el listado de correcciones
        // (nueva propuesta del empleado, aprobación, rechazo). El front
        // refetchea el badge del TopBar y — si el panel está abierto —
        // la lista queda actualizada.
        {
          id: `employee-corrections/${user.id}`,
          operator: () => dispatch(pollEmployeeCorrectionsCount({ force: true })),
        },
        // Push del backend cuando el TaskMicro detecta nuevas alertas de
        // fichaje (SIN_CHECKOUT / no fichó entrada). Refetchea el resumen
        // que alimenta el badge.
        {
          id: `schedule-alerts/${user.id}`,
          operator: () => dispatch(fetchScheduleAlerts({ force: true })),
        },
        // El backend bloqueó un WhatsApp por cuota agotada → banner global.
        {
          id: `whatsapp-quota-exhausted/${user.id}`,
          operator: () =>
            window.dispatchEvent(new CustomEvent("tmo:whatsapp-quota-exhausted")),
        },
      ],
      dispatch,
      connectToHubNotificationRestaurante,
      "notification-restaurante"
    );

    return () => {
      cleanup(); // Asegura que la conexión se cierra al desmontar
      isConnectedRef.current = false;
    };
  }, [user?.id, dispatch, handleReceiveNotification]);
}

export function useNotificationSessionHub() {
  const dispatch = useDispatch();
  const { session } = useSelector((state) => state.authJwt);
  const isConnectedRef = useRef(false); // Para evitar múltiples conexiones

  // Función optimizada para manejar la recepción de notificaciones
  const handleReceiveSessionNotification = useCallback(
    (notification) => {
      dispatch(receiveNotificationFromHubSuccess(notification));
      notifyme(notification, (id) => dispatch(readNotification(id)));
    },
    [dispatch]
  );

  // Función optimizada para manejar pedidos listos
  const handlePedidoReady = useCallback(
    (pedido) => {
      dispatch(addNotificationsReady(pedido));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!session?.id || isConnectedRef.current) return;

    isConnectedRef.current = true; // Evita conexiones repetidas

    const cleanup = CreateConnection(
      buildHubUrl("/hub/notificationsessionhub", { sessionId: session.id }),
      [
        {
          id: `notification/session/${session.id}`,
          operator: handleReceiveSessionNotification,
        },
        {
          id: `notification/session/${session.id}/isReady`,
          operator: handlePedidoReady,
        },
      ],
      dispatch,
      connectToHubNotificationSession,
      "notification-session"
    );

    return () => {
      cleanup(); // Asegura que la conexión se cierra al desmontar
      isConnectedRef.current = false;
    };
  }, [
    session?.id,
    dispatch,
    handleReceiveSessionNotification,
    handlePedidoReady,
  ]);
}
